/**
 * User Context Initializer
 *
 * APP_INITIALIZER function that loads the user context before the app renders.
 *
 * Behavior:
 * - In embedded mode: Attempts to load user context from host with retry logic
 * - On failure after retries: Falls back to read-only mode with an alert
 * - In standalone mode: No user is set; workspaces use their own mock profiles via component inputs
 */

import { inject, Injector, runInInjectionContext } from '@angular/core';
import { UserService } from '../services/user.service';
import { ReadOnlyModeService } from '../services/read-only-mode.service';
import { HOST_BRIDGE } from '../services/host-bridge/host-bridge.factory';

/**
 * Configuration for retry behavior
 */
const RETRY_CONFIG = {
  maxAttempts: 3,
  baseDelayMs: 1000, // 1 second
  backoffMultiplier: 2, // exponential backoff: 1s, 2s, 4s
};

/**
 * Sleep utility for retry delays
 */
function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

/**
 * Factory function for APP_INITIALIZER.
 *
 * This function is called during app bootstrap and must complete
 * before the app renders.
 *
 * @example
 * ```typescript
 * // In app.config.ts
 * export const appConfig: ApplicationConfig = {
 *   providers: [
 *     hostBridgeProvider,
 *     {
 *       provide: APP_INITIALIZER,
 *       useFactory: userContextInitializerFactory,
 *       multi: true
 *     }
 *   ]
 * };
 * ```
 */
export function userContextInitializerFactory() {
  const injector = inject(Injector);

  return async (): Promise<void> => {
    await runInInjectionContext(injector, async () => {
      const userService = inject(UserService);
      const readOnlyModeService = inject(ReadOnlyModeService);
      const hostBridge = inject(HOST_BRIDGE);

      if (hostBridge.isEmbedded()) {
        // Embedded mode - load user from host with retry logic
        await loadUserWithRetry(userService, readOnlyModeService);
      } else {
        // Standalone mode - don't auto-set a user
        // Each workspace defines its own mock user via component inputs (e.g., HeaderComponent's user input)
        // This preserves backward compatibility with pre-ServiceNow integration behavior
        console.log('[UserContextInit] Standalone mode - using workspace-defined mock user');
      }
    });
  };
}

/**
 * Attempt to load user context with retry logic and exponential backoff.
 * Falls back to read-only mode if all attempts fail.
 */
async function loadUserWithRetry(
  userService: UserService,
  readOnlyModeService: ReadOnlyModeService
): Promise<void> {
  let lastError: Error | null = null;

  for (let attempt = 1; attempt <= RETRY_CONFIG.maxAttempts; attempt++) {
    try {
      console.log(`[UserContextInit] Attempting to load user context (attempt ${attempt}/${RETRY_CONFIG.maxAttempts})...`);

      await userService.loadUserFromHost();

      console.log('[UserContextInit] User context loaded successfully');
      return; // Success - exit the retry loop
    } catch (error) {
      lastError = error instanceof Error ? error : new Error(String(error));
      console.warn(`[UserContextInit] Attempt ${attempt} failed:`, lastError.message);

      // Wait before retry (except on last attempt)
      if (attempt < RETRY_CONFIG.maxAttempts) {
        const delayMs = RETRY_CONFIG.baseDelayMs * Math.pow(RETRY_CONFIG.backoffMultiplier, attempt - 1);
        console.log(`[UserContextInit] Retrying in ${delayMs}ms...`);
        await sleep(delayMs);
      }
    }
  }

  // All attempts failed - fall back to read-only mode
  console.error('[UserContextInit] All attempts to load user context failed. Entering read-only mode.');

  // Set a limited guest user
  userService.setUser({
    name: 'Guest User',
    role: 'Viewer',
    avatar: 'user-avatars/default.png',
    email: '',
    roles: ['viewer'],
  });

  // Enable read-only mode with explanation
  const reason = `Unable to verify your user permissions. Some features may be unavailable. ${
    lastError ? `Error: ${lastError.message}` : ''
  }`;
  readOnlyModeService.enableReadOnly(reason);
}

/**
 * Convenience provider configuration for user context initialization.
 *
 * @example
 * ```typescript
 * // In app.config.ts
 * import { userContextInitializerProvider } from 'shared';
 *
 * export const appConfig: ApplicationConfig = {
 *   providers: [
 *     hostBridgeProvider,
 *     userContextInitializerProvider,
 *   ]
 * };
 * ```
 */
import { APP_INITIALIZER } from '@angular/core';

export const userContextInitializerProvider = {
  provide: APP_INITIALIZER,
  useFactory: userContextInitializerFactory,
  multi: true,
};
